/**
 * What the portal says while auto-prep gets an order ready (docs/autoprep/QUEUE.md, Ryan's rulings R2 and R3).
 *
 * The server decides everything (`project.autoprep`, backend app/autoprep_queue/rules.py row_view): a client or an
 * editor is told only `{held: true}`, and only while the order is held; admin and staff get the step. This module only
 * words it, so the board row, the drawer and the Download footage button cannot say three different things.
 *
 * EDITOR_MESSAGE and CLIENT_LABEL are the ruling's own words and must match the server's
 * (backend tests/test_autoprep_queue_rules.py reads this file to check).
 */
import { keepsLink, KEEP_TAIL } from './keepLink.js';

export const EDITOR_MESSAGE = "We're getting this project ready. The footage download opens when it's done.";
export const CLIENT_LABEL = 'Preparing your footage';
export const PENDING_LABEL = 'Auto-prep pending';

/** The row chip on the board, or null. The team's says "Auto-prep pending" and the step, on Project Requested and
 *  Assigned to Editor rows (R2, R25); an editor's says the editor sentence; a client's says "Preparing your footage". */
export function prepChip(project, role) {
  const a = project?.autoprep;
  if (!a?.held) return null;
  if (role === 'admin' || role === 'staff') {
    if (project.admin_status_id !== 1 && project.admin_status_id !== 3) return null;
    const tone = a.state === 'failed' ? 'red' : a.stalled ? 'amber' : 'teal';
    const title = a.state === 'failed'
      ? 'Auto-prep stopped on this order. Open the project: the Files tab says what happened and what to do.'
      : a.stalled
        ? 'The prep computer stopped checking in. It carries on by itself when it is back; the Files tab has the details.'
        : project.admin_status_id === 3
          ? 'The editor cannot download the footage while the prep computer gets it ready, for at most 36 hours.'
          : 'The order stays at Project Requested while the prep computer gets the footage ready, for at most 72 hours.';
    return { kind: 'team', label: `${PENDING_LABEL} · ${a.step}`, tone, title };
  }
  if (role === 'editor') return { kind: 'editor', label: EDITOR_MESSAGE, tone: 'teal', title: EDITOR_MESSAGE };
  // The label stays the ruling's words (R2, R3); the tooltip adds the keep ask when the footage is the client's own link
  // before its first draft (lib/keepLink.js): "preparing your footage" must not read as "we have it now".
  if (role === 'client') return { kind: 'client', label: CLIENT_LABEL, tone: 'teal',
    title: `We are getting your footage ready for your editor.${keepsLink(project) ? ` ${KEEP_TAIL}` : ''}` };
  return null;
}

/** "4 h 10 min", "25 min", "less than a minute": how long until the hold's time limit lets the order go, from now. */
export function timeLeft(releaseAt, now = Date.now()) {
  const ms = new Date(releaseAt).getTime() - now;
  if (!(ms > 60000)) return 'less than a minute';
  const mins = Math.floor(ms / 60000);
  const h = Math.floor(mins / 60), m = mins % 60;
  return h ? `${h} h${m ? ` ${m} min` : ''}` : `${m} min`;
}

/** "3 weddings, 4 orders": the prep computer's work counted the way it does it, a wedding at a time (R18), with the
 *  orders those weddings make a package for. `rows` are the settings card's rows, each with its `orders`. */
export function weddingCount(rows) {
  const weddings = rows.length;
  const orders = rows.reduce((n, r) => n + (r.orders?.length || 0), 0);
  return `${weddings} wedding${weddings === 1 ? '' : 's'}, ${orders} order${orders === 1 ? '' : 's'}`;
}

/** One order of a wedding as the lists name it: its product, with its own title when that is not the wedding's. */
export function orderName(order, weddingTitle) {
  const product = order.product || 'Order';
  return order.title && order.title !== weddingTitle ? `${product} (${order.title})` : product;
}

/** The settings card's line about the prep computer (R21): the name people know it by (the host name it last reported;
 *  its id only until it has said one) and when it last checked in. `when` words the time (lib/format's formatEastern). */
export function stationLine(station, when) {
  if (!station) return 'No prep computer has checked in yet.';
  return `Prep computer ${station.label || station.station} last checked in ${when(station.seen_at)}.`;
}

/** How long ago, in words: "12 seconds", "1 minute", "3 hours". Never negative (a clock a little ahead reads as now). */
export function agoText(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s} second${s === 1 ? '' : 's'}`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`;
  const h = Math.floor(m / 60);
  return `${h} hour${h === 1 ? '' : 's'}`;
}

// The worker checks in every 30 seconds, whatever it is doing (tools/autoprep/worker.py TICK_SECONDS, its heartbeat).
// Six missed check-ins and the status screen says it has gone quiet.
export const QUIET_AFTER_SECONDS = 180;

/** The status screen's one word for the prep computer (pages/AdminPrepStatusPage.js), from the settings card's data
 *  (GET /admin/autoprep-queue). In this order, the first that holds:
 *    none     no prep computer has ever checked in
 *    silent   its last check-in is older than QUIET_AFTER_SECONDS (off, asleep, the worker stopped, no network)
 *    paused   it says why it has stopped (its drive unplugged, R22)
 *    working  it is downloading, prepping or uploading a wedding
 *    off      checking in, but "Run auto-prep" is switched off
 *    starting the queue is on and weddings are waiting, none started yet (it takes the next one within 30 seconds)
 *    person   nothing to do but weddings stopped and waiting for a person
 *    idle     on, checking in, nothing to prep
 *  `tone`: 'good', 'quiet' or 'warning' (a warning always carries its words, never colour alone). */
export function prepStatus(data, nowMs = Date.now()) {
  const station = (data?.stations || [])[0];
  const rows = data?.running || [];
  const working = rows.filter(r => ['downloading', 'prepping', 'uploading'].includes(r.state));
  const waiting = rows.filter(r => r.state === 'waiting');
  const stopped = rows.filter(r => r.state === 'failed');
  const finished = data?.finished || [];      // prepared most recently (GET /admin/autoprep-queue `finished`)
  // the screen's three lanes, left to right
  const lane = state => working.filter(r => r.state === state);
  const base = { working, waiting, stopped, finished, station,
                 downloading: lane('downloading'), prepping: lane('prepping'), uploading: lane('uploading') };
  if (!station) return { ...base, key: 'none', word: 'No prep computer', tone: 'warning', sub: 'No prep computer has checked in yet.' };
  const age = (nowMs - Date.parse(station.seen_at)) / 1000;
  if (!(age <= QUIET_AFTER_SECONDS)) {
    return { ...base, key: 'silent', word: 'Not checking in', tone: 'warning',
             sub: `Last heard from ${agoText(age)} ago. Check that the prep computer is on, awake and online.` };
  }
  if (station.problem) return { ...base, key: 'paused', word: 'Paused', tone: 'warning', sub: station.problem };
  if (working.length) {
    return { ...base, key: 'working', word: 'Working', tone: 'good',
             sub: working.length === 1 ? 'On 1 wedding now.' : `On ${working.length} weddings now.` };
  }
  if (!data.queue) return { ...base, key: 'off', word: 'Switched off', tone: 'quiet', sub: 'Run auto-prep is off. The prep computer is on and checking in.' };
  if (waiting.length) return { ...base, key: 'starting', word: 'Starting next', tone: 'good', sub: 'It takes the next wedding within 30 seconds.' };
  if (stopped.length) return { ...base, key: 'person', word: 'Needs a person', tone: 'warning', sub: 'Nothing else to do: the weddings below stopped and are waiting for someone.' };
  return { ...base, key: 'idle', word: 'Nothing to prep', tone: 'quiet', sub: 'It looks for new orders every 30 seconds.' };
}

/** The status screen's headline (2026-10-15, the look Ryan shared): "Working on 3 weddings" while it works, counting the
 *  weddings downloading, prepping or uploading; otherwise the status's own word ("Nothing to prep", "Paused", ...). */
export function prepHeadline(status) {
  if (status?.key !== 'working') return status?.word || '';
  const n = status.working.length;
  return `Working on ${n} wedding${n === 1 ? '' : 's'}`;
}

/** How long ago, short, for the status screen's rows and its check-in: "just now", "12 sec ago", "4 min ago", "2 h ago",
 *  "1 day ago", "3 days ago". Never negative (a clock a little ahead reads as now). */
export function agoShort(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 10) return 'just now';
  if (s < 60) return `${s} sec ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

/** A column's caption for how many the prep computer runs at once (the settings card's downloads and preps, R5):
 *  "one at a time", "2 at once"; null when the setting is not known. */
export function atOnce(n) {
  if (!(n >= 1)) return null;
  return n === 1 ? 'one at a time' : `${n} at once`;
}

/** A Ready row's "prepped in 1 h 12 min", from the time its job spent prepping (GET /admin/autoprep-queue `finished`:
 *  `prepped_seconds`, backend routes/autoprep_queue.py prepped_time), with "(in 2 parts)" when a Resume carried it on.
 *  Null when the history records no prepping: nothing is said rather than a made-up number. */
export function preppedText(row) {
  const s = row?.prepped_seconds;
  if (typeof s !== 'number' || !(s >= 0)) return null;
  return `prepped in ${durationText(s)}${row.prepped_parts > 1 ? ` (in ${row.prepped_parts} parts)` : ''}`;
}

/** The Prepping column, top to bottom: the weddings being prepared or uploaded (an upload is the prep's last, short step,
 *  so it stays where its prep was), in the portal's order; then those downloaded and waiting for a free prep slot (the
 *  prep Mac's words in `progress.detail`, or a training re-prep downloaded ahead: trainingStep's `ready`). */
export function prepColumn(status) {
  const rows = [...(status?.prepping || []), ...(status?.uploading || [])];
  const order = new Map((status?.working || []).map((r, i) => [r.project_id, i]));
  const at = r => order.get(r.project_id) ?? 0;
  const waits = r => r.state === 'prepping' && (r.kind === 'training' ? trainingStep(r).ready : !!r.progress?.detail);
  return [...rows.filter(r => !waits(r)).sort((a, b) => at(a) - at(b)), ...rows.filter(waits).sort((a, b) => at(a) - at(b))];
}

/** What needs a person, for the status screen's "Needs you" band, in this order: the prep computer itself when it has
 *  paused (its own reason) or stopped checking in; each failed wedding (its error, when, and the footage it keeps on the
 *  drive: R6, until someone resumes, retries or gives it up); each prep or upload that has not moved for an hour (the
 *  admins are emailed for these, rules.stuck). `nowMs` on the portal's clock. Empty when nothing needs anyone. */
export function needsYou(status, nowMs = Date.now()) {
  const out = [];
  if (status?.key === 'silent' || status?.key === 'paused') {
    out.push({ key: 'computer', kind: 'computer', title: status.station?.label || status.station?.station || 'The prep computer', why: status.sub });
  }
  for (const r of status?.stopped || []) {
    out.push({ key: r.project_id, kind: 'failed', project_id: r.project_id, title: r.title || 'Untitled', orders: r.orders || [],
               why: r.error || 'It stopped without saying why.', at: r.failed_at || null, gb: typeof r.gb === 'number' && r.gb > 0 ? r.gb : null });
  }
  for (const r of status?.working || []) {
    if (r.kind === 'training') continue;   // a training re-prep's troubles are the training page's (withTraining)
    const stuck = prepLine(r, nowMs)?.stuck ? prepLine(r, nowMs).text : uploadStuck(r, nowMs);
    if (stuck) out.push({ key: r.project_id, kind: 'stuck', project_id: r.project_id, title: r.title || 'Untitled', orders: r.orders || [], why: stuck });
  }
  return out;
}

/** The percentage in a step's words ("Downloading 42%" -> 42), or null. */
export function stepPercent(step) {
  const m = /(\d{1,3})%/.exec(step || '');
  return m ? Math.min(100, Number(m[1])) : null;
}

/** Bytes as the status screen says them, in the decimal units the link check and the drive use: "850 MB", "3.5 GB",
 *  "296 GB". */
export function sizeText(bytes, fine = false) {
  const gb = (bytes || 0) / 1e9;
  if (gb < 1) return `${Math.round((bytes || 0) / 1e6)} MB`;
  // `fine`: what a download has so far, to a tenth of a GB at any size, so it visibly moves (a total stays whole)
  if (fine) return `${(Math.floor(gb * 10) / 10).toFixed(1)} GB`;
  return gb < 10 ? `${gb.toFixed(1)} GB` : `${Math.round(gb)} GB`;
}

/** A percentage for the status screen, to a tenth below 100 (a 296 GB download moves 1% per 3 GB, so a whole number
 *  sits still for a minute and looks stuck), rounded down so it never says done early. Never more than 100. */
export function percentText(pct) {
  const p = Math.min(100, Math.max(0, pct));
  return p >= 100 ? '100%' : `${(Math.floor(p * 10) / 10).toFixed(1)}%`;
}

/** Does this working wedding's card count bytes of a total: a download, and an upload once the prep computer has said
 *  what it has sent (tools/autoprep/worker.py upload_report, the same shape)? Then it shows what a download shows: the
 *  GB, the speed, the time left and the percent. */
export function countsBytes(row) {
  return row?.state === 'downloading' || (row?.state === 'uploading' && typeof row?.progress?.bytes === 'number');
}

// The speed is read over the last few minutes of check-ins: long enough to steady it, short enough to follow a change.
export const RATE_WINDOW_MS = 5 * 60 * 1000;

/** Download speed in bytes a second (an upload's too), from a wedding's samples [{at: ms, bytes}] oldest first (one per
 *  check-in that recorded progress): over the samples inside RATE_WINDOW_MS of the newest, at least 50 seconds apart
 *  (two check-ins at the least), else null. A download that went backwards (started again) has no speed yet. */
export function downloadRate(samples) {
  if (!samples || samples.length < 2) return null;
  const last = samples[samples.length - 1];
  const first = samples.find(s => last.at - s.at <= RATE_WINDOW_MS);
  const seconds = (last.at - first.at) / 1000;
  if (!(seconds >= 50) || last.bytes < first.bytes) return null;
  return (last.bytes - first.bytes) / seconds;
}

// Between the prep computer's reports (every 5 seconds since 2026-10-09: worker.PROGRESS_SECONDS) the status screen
// moves a download on at its measured speed, so the GB and the bar move every second, and the next report corrects it.
// Never more than LIVE_CAP_MS past the last report (a Mac that stopped reporting is not counted forward), never past the
// total, and only with a speed (none while it is still being measured).
export const LIVE_CAP_MS = 15000;

/** `progress` {bytes, total, pct} as the prep computer reported it at `progressAt` (ISO, the portal's clock), moved on
 *  at `rate` bytes a second (downloadRate) to `nowMs` (the portal's clock now): the same shape. Unchanged without a
 *  speed, a byte count or a report time. Its percent stops at 99, as the prep computer's own does until it is done.
 *  `capMs`: how far past the report it may count (LIVE_CAP_MS; a training re-prep's download reports as often since
 *  R128, so the same). */
export function liveProgress(progress, progressAt, rate, nowMs, capMs = LIVE_CAP_MS) {
  const p = progress || {};
  const at = Date.parse(progressAt || '');
  if (typeof p.bytes !== 'number' || !(rate > 0) || !Number.isFinite(at)) return p;
  const seconds = Math.min(capMs, Math.max(0, nowMs - at)) / 1000;
  const bytes = p.total ? Math.min(p.total, p.bytes + rate * seconds) : p.bytes + rate * seconds;
  return { ...p, bytes, pct: p.total ? Math.min(99, (100 * bytes) / p.total) : p.pct };
}

/** "about 2 h 10 min left", "about 40 min left", "under a minute left". */
export function timeLeftText(seconds) {
  const m = Math.round(seconds / 60);
  if (m < 1) return 'under a minute left';
  if (m < 60) return `about ${m} min left`;
  const h = Math.floor(m / 60), r = m % 60;
  return `about ${h} h${r ? ` ${r} min` : ''} left`;
}

/** The line under a downloading wedding on the status screen: "3.5 of 296 GB · 45 MB/s · about 1 h 50 min left"; and
 *  under an uploading one (countsBytes), what it has sent of its packages. `rate` from downloadRate (null while it is
 *  still measuring); 0 says it is not moving. Null with nothing moved and no total, so the screen shows no empty line. */
export function downloadLine(progress, rate) {
  const { bytes, total } = progress || {};
  if (!total && !bytes) return null;
  const got = sizeText(bytes || 0, true), all = total ? sizeText(total) : null;
  // "3.5 of 296 GB" when both are in GB, "850 MB of 296 GB" when they are not
  const both = all && got.endsWith(' GB') ? `${got.slice(0, -3)} of ${all}` : `${got} of ${all}`;
  const parts = [all ? both : `${got} so far`];
  if (rate === 0) parts.push('not moving');
  else if (rate > 0) {
    parts.push(rate < 1e6 ? `${(rate / 1e6).toFixed(1)} MB/s` : `${Math.round(rate / 1e6)} MB/s`);
    if (total && total > (bytes || 0)) parts.push(timeLeftText((total - (bytes || 0)) / rate));
  } else parts.push('measuring speed');
  return parts.join(' · ');
}

/** A working wedding's step for the status screen: "Downloading 1.2%" or "Uploading 80.4%" from the progress the prep
 *  Mac reported (finer than the board's whole-number step), else the step as the board says it. */
export function stepLabel(row) {
  const pct = row?.progress?.pct;
  const word = { downloading: 'Downloading', uploading: 'Uploading' }[row?.state];
  return word && typeof pct === 'number' ? `${word} ${percentText(pct)}` : (row?.step || '');
}

/** The footage the failed weddings keep on the prep computer's drive (each row's `gb`), in whole GB: null when no size
 *  is known. A failed wedding keeps its footage until someone resumes, retries or gives it up (R6). */
export function heldGb(rows) {
  const known = (rows || []).map(r => r.gb).filter(g => typeof g === 'number' && g > 0);
  return known.length ? Math.round(known.reduce((a, b) => a + b, 0)) : null;
}

/** When something happened, small, beside "2 hours ago" on the status screen: "Oct 9, 4:32 PM" in New York, the
 *  portal's own time (lib/format formatEastern, without the year and "ET"). "" when there is no time. */
export function shortWhen(iso) {
  const d = new Date(iso || '');
  if (!iso || Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

/** An editor due date ("2026-10-02") as the line shows it: "due Oct 2", and whether that day has passed in New York.
 *  Null without a date. */
export function dueText(due, nowMs = Date.now()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(due || '');
  if (!m) return null;
  const day = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  const today = new Date(nowMs).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });   // YYYY-MM-DD
  return { text: `due ${day.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })}`,
           overdue: `${m[1]}-${m[2]}-${m[3]}` < today };
}

/** How long a wedding has been on its current step, for the status screen: "25 min so far" while it is being prepared or
 *  uploaded (a download has its own line, downloadLine), from the step's start (`state_at`); null without. */
export function stepSince(row, nowMs = Date.now()) {
  if (!row || !['prepping', 'uploading'].includes(row.state) || !row.state_at) return null;
  const t = Date.parse(row.state_at);
  return Number.isFinite(t) ? `${durationText(Math.max(0, (nowMs - t) / 1000))} so far` : null;
}

// A running prep's stage as the card says it (backend app/autoprep_queue/rules.py STAGE_WORDS, the same words, held equal
// by backend/tests/test_autoprep_queue_rules.py). "cut" is the multicam auto-cut, said so (R133).
export const STAGE_WORDS = { unpack: 'Unpacking the zips', inventory: 'Listing the footage', sync: 'Syncing cameras and sound', transcribe: 'Transcribing',
  frames: 'Reading the frames', plan: 'Planning the day', cut: 'Cutting between cameras', color: 'Matching color', output: 'Writing the project',
  finishing: 'Finishing', film: 'Making the films' };
export const STUCK_MINUTES = 60;          // rules.STUCK_MINUTES: an hour without moving and the card says so

const num = (n, unit) => (unit === 'GB read' ? Math.round(n * 10) / 10 : Math.round(n)).toLocaleString('en-US');
const minText = (m) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`);

/** A length of time, to the nearest minute: "under a minute", "25 min", "1 h 12 min", "3 h". Null when it is not a
 *  number. */
export function durationText(seconds) {
  if (!Number.isFinite(seconds)) return null;
  return seconds < 60 ? 'under a minute' : minText(Math.round(seconds / 60));
}

/** The words of a step that has stopped moving, "No progress for 1 h 20 min · Transcribing": when the portal says it is
 *  stuck (rules.stuck), or the prep computer's own "last moved" (progress.moved_at) is STUCK_MINUTES old. Null while it
 *  moves. */
function stuckText(row, what, nowMs) {
  const moved = Date.parse(row.progress?.moved_at || '');
  const still = Number.isFinite(moved) ? Math.max(0, Math.floor((nowMs - moved) / 60000)) : 0;
  return row.stuck || still >= STUCK_MINUTES ? `No progress for ${minText(still)} · ${what}` : null;
}

/** An upload that has stopped moving, for its card: "No progress for 1 h 5 min · Uploading". Null while it moves (its
 *  card then says what it has sent, downloadLine) and for anything that is not an upload. */
export function uploadStuck(row, nowMs = Date.now()) {
  return row?.state === 'uploading' ? stuckText(row, 'Uploading', nowMs) : null;
}

/** A running prep on the status screen (2026-10-11, Ryan: "is there any way at all to know how long is left on a
 *  prep"), from what the prep Mac reports (tools/autoprep/worker.py prep_report):
 *    "Transcribing · 250 of 1,000 min of audio · 2 h so far · about 1 h 15 min left (55 min to 1 h 50 min)"
 *  ("so far" from the prep's own start, not its wait for a slot), and, when it has not moved for STUCK_MINUTES,
 *  { stuck: true } with "No progress for 1 h 20 min · Transcribing". `stage` is the first part alone and `rest` the
 *  others (the card puts the stage at its top right); `pct` is the share done by time (started vs time left), for the
 *  bar. Null for anything that is not a running prep with a stage (a wedding waiting for a slot keeps its own words). */
export function prepLine(row, nowMs = Date.now()) {
  const p = row?.progress || {};
  if (row?.state !== 'prepping' || !p.stage || p.detail) return null;
  const f = prepFigures(p, nowMs);
  let stage = STAGE_WORDS[p.stage] || 'Prepping';
  if (p.stage === 'film' && f.counted) stage = `Making film ${Math.round(p.done) + 1} of ${Math.round(p.of)}`;
  const stuck = stuckText(row, stage, nowMs);
  if (stuck) return { text: stuck, stuck: true, pct: null, stage, rest: stuck };
  const parts = [stage];
  if (f.count) parts.push(f.count);
  if (Number.isFinite(f.started)) parts.push(`${durationText(Math.max(0, (nowMs - f.started) / 1000))} so far`);
  if (f.left) parts.push(f.left);
  return { text: parts.join(' · '), stuck: false, pct: f.pct, stage, rest: parts.slice(1).join(' · ') };
}

/** A running analysis's own figures, as the prep computer reports them (tools/autoprep/worker.py prep_report): the
 *  count inside its stage (`count`, "250 of 1,000 min of audio"; none for the films, which their stage's words count),
 *  the time left (`left`, "about 1 h 15 min left (55 min to 1 h 50 min)", "almost done") and the share done by time for
 *  the bar (`pct`, 1 to 99: the time since the prep's own start against that plus the time left). Each null when it was
 *  not reported; `counted` and `started` (ms, NaN without) for the caller's own words. One reading for a live prep's
 *  line (prepLine) and a training re-prep's analysis (trainingStep, R128). */
export function prepFigures(progress, nowMs = Date.now()) {
  const p = progress || {};
  const counted = typeof p.done === 'number' && typeof p.of === 'number' && p.of > 0;
  const count = counted && p.stage !== 'film' ? `${num(p.done, p.unit)} of ${num(p.of, p.unit)}${p.unit ? ` ${p.unit}` : ''}` : null;
  const started = Date.parse(p.started_at || '');
  let left = null, pct = null;
  if (typeof p.eta_min === 'number') {
    left = p.eta_min < 1 ? 'almost done' : `about ${minText(p.eta_min)} left` +
      (typeof p.eta_lo_min === 'number' && typeof p.eta_hi_min === 'number' && p.eta_hi_min > p.eta_lo_min
        ? ` (${minText(p.eta_lo_min)} to ${minText(p.eta_hi_min)})` : '');
    if (Number.isFinite(started)) {
      const ran = (nowMs - started) / 60000;
      pct = Math.max(1, Math.min(99, (100 * ran) / (ran + p.eta_min)));
    }
  }
  return { counted, count, started, left, pct };
}

// ── Training re-preps, live (Ryan, 2026-10-24: "can i see live whats being processed there?" and, for the status
// screen, "a tag for which ones are ai training and which ones are processing because they are current real jobs") ──

/** A row's key for its card and its speed samples: a live wedding by its project, a training re-prep by its own id, a
 *  Shutter & Sound job (R275, ssLanes) by its wedding and kind. */
export const rowKey = (r) => (r?.kind === 'training' ? `training:${r.id}` : r?.kind === 'ss' ? `ss:${r.wedding}:${r.job}` : r?.project_id);

/** Each byte-counting row's speed samples after an answer (the status screen's and the training page's): `prev` and the
 *  result are {rowKey: [{at: ms, bytes}]}, oldest first, inside RATE_WINDOW_MS of the newest; a report already sampled
 *  (the same `progress_at`) is not sampled twice. Only rows that count bytes (countsBytes) with a byte count and a
 *  report time; every other row's samples are dropped. */
export function nextSamples(prev, rows) {
  const next = {};
  for (const row of rows || []) {
    const { bytes } = row.progress || {};
    const at = Date.parse(row.progress_at || '');
    if (!countsBytes(row) || typeof bytes !== 'number' || !at) continue;
    const key = rowKey(row);
    const had = ((prev || {})[key] || []).filter(x => at - x.at <= RATE_WINDOW_MS);
    next[key] = had.length && had[had.length - 1].at === at ? had : [...had, { at, bytes }];
  }
  return next;
}

// A training re-prep's download reports every 5 seconds between the prep Mac's looks, as a live download does (R128,
// tools/autoprep/worker.py training_report_progress), so the screens count it on with the live cap (LIVE_CAP_MS) and
// measure its speed from the same samples (downloadRate, nextSamples). Its analysis reports at each look (30 seconds), as
// a live prep's does, with the same figures (prepFigures).

// A training re-prep's steps, in order (backend app/autoprep_queue/training_preps.py: downloading, preparing, the AI cut
// while the footage is still there, then archiving). The AI cut is a step only when the re-prep makes one (`ai_cut`).
export const TRAINING_STEPS = [
  { key: 'download', word: 'Download', full: 'Downloading the footage' },
  { key: 'analysis', word: 'Analysis', full: 'Preparing the analysis' },
  { key: 'ai-cut', word: 'AI cut', full: 'Making the AI cut' },
  { key: 'keep', word: 'Keep', full: 'Keeping the analysis' },
];
const PAUSED_PREFIX = /^Paused for live work:\s*/i;
// A paused re-prep in the Mac's own words (its check-in's `detail`): paused for live work, and why (no words: live work,
// as before), or waiting on something of its own, said as the Mac said it ("Waiting a moment: the prep computer is
// updating itself", "Waiting for a free training slot"), `waiting`. Every pause read "Paused for live work" before: on
// 2026-11-01 the status screen said it of Monica & Jared, held for the prep Mac's update, with no live work at all.
function pausedWords(row) {
  const said = String(row?.detail || '');
  if (!said || PAUSED_PREFIX.test(said)) {
    return { word: 'Paused for live work', why: said.replace(PAUSED_PREFIX, '') || 'live work comes first', waiting: false };
  }
  return { word: said, why: null, waiting: true };
}
// A finished re-prep's step under way (backend app/training_autopilot.py side_rows; Ryan, 2026-10-30: the status screen
// said "Nothing to prep" through a night of picture passes): its picture pass (R194) or its AI cut made again (R164)
export const SIDE_STEPS = {
  picture: { word: 'Pictures', full: 'Reading its pictures', what: 'for the shot model, from its kept folder' },
  remake: { word: 'AI cut again', full: 'Making its AI cut again', what: 'from its kept folder' },
  // a delivered live job's picture read, before its footage goes (R208, backend training_autopilot.after_reads)
  'after-read': { word: 'Pictures', full: 'Reading its pictures', what: 'for the shot model, after its delivery' },
};

/** Where a training re-prep is, for its card: the step (`key`, `word` in full), the analysis's own stage in words
 *  ("Transcribing", STAGE_WORDS) while it is prepared, the steps in order each 'done', 'now' or 'next', how long it has
 *  been on its step (`onStep`, from the step's start: the download and keeping from the move into it, the analysis from
 *  its start (`began_at`, R268; before it, the move into it), the AI cut from the report that first named it), how long
 *  it waited for a prep lane before its analysis began (`waited`, R268) and on the analysis's stage (`onStage`), and, paused for
 *  live work, why (the prep Mac's own words); paused for anything else (`waiting`), its words in full (pausedWords). While the analysis runs, what a live prep's card says of it (R128,
 *  prepFigures): the stage's `count`, the time `left` and the step's `pct` by time; null otherwise (a download's bytes
 *  are downloadLine's). Downloaded ahead and waiting for a slot (DOWNLOAD AHEAD, 2026-10-31: the prep Mac's worker
 *  says it as the stage 'downloaded'), `ready`: "Downloaded", its download done and the analysis next, never "Paused for
 *  live work". `row` is GET /admin/training's `running` row or the status screen's (withTraining: `training_state`).
 *  `nowMs` on the portal's clock. */
export function trainingStep(row, nowMs = Date.now()) {
  const st = row?.training_state || row?.state;
  const side = SIDE_STEPS[st];
  if (side) {        // a picture pass, remake or after-delivery read: one step, how long it has run, or paused and why
    const t = Date.parse(row?.since || '');
    const paused = !!row?.paused;
    const pw = pausedWords(row);
    return { key: st, paused, waiting: paused && pw.waiting, word: paused ? pw.word : side.full, stage: null,
             why: paused ? pw.why : null,
             steps: [{ key: st, word: side.word, state: 'now' }],
             onStep: !paused && Number.isFinite(t) ? durationText(Math.max(0, (nowMs - t) / 1000)) : null, onStage: null,
             count: paused ? null : side.what, left: null, pct: null };
  }
  // paused for live work, or between two steps, the Mac names no stage: the last one it named (`stage_last`) still says
  // which step a prepared re-prep is on (the AI cut, not the analysis); a download or keeping is its state alone
  const stage = row?.progress?.stage ?? row?.stage ?? row?.stage_last ?? null;
  const key = st === 'downloading' ? 'download' : st === 'archiving' ? 'keep' : stage === 'ai cut' ? 'ai-cut' : 'analysis';
  const steps = TRAINING_STEPS.filter(s => s.key !== 'ai-cut' || row?.ai_cut || key === 'ai-cut');
  const at = steps.findIndex(s => s.key === key);
  const ago = (iso) => {
    const t = Date.parse(iso || '');
    return Number.isFinite(t) ? durationText(Math.max(0, (nowMs - t) / 1000)) : null;
  };
  const still = !!row?.paused;
  const ready = still && key === 'analysis' && stage === 'downloaded';     // downloaded ahead, the next to prep
  const paused = still && !ready;
  const pw = pausedWords(row);
  const sub = key === 'analysis' && stage ? (STAGE_WORDS[stage] || null) : null;
  const fig = key === 'analysis' && !still ? prepFigures(row?.progress, nowMs) : null;
  // when its analysis began (R268, backend training_preps.heartbeat `began_at`): the analysis counts from then, and the
  // time before it, from its move into preparing, was its wait for a free prep lane ("waited 9 h 53 min for a turn"). A
  // row without it (its analysis under way before R268) counts from the move, as before.
  const began = key === 'analysis' ? Date.parse(row?.began_at || '') : NaN;
  const moved = Date.parse(row?.since || '');
  const waited = !still && Number.isFinite(began) && Number.isFinite(moved) && began - moved >= 60000
    ? durationText((began - moved) / 1000) : null;
  // the AI cut step downloading an editor's film for its small preview (R130): its bytes of its total, as a count and a share
  const dl = key === 'ai-cut' && !still ? editorDownload(row?.progress) : null;
  return {
    key, paused, ready, waiting: paused && pw.waiting, word: ready ? 'Downloaded' : paused ? pw.word : steps[at].full,
    stage: still ? null : sub, why: paused ? pw.why : null,
    steps: steps.map((s, i) => ({ key: s.key, word: s.word, state: i < at ? 'done' : i === at && !ready ? 'now' : 'next' })),
    onStep: still ? null : ago(key === 'ai-cut' ? (row?.stage_at || row?.since) : Number.isFinite(began) ? row.began_at : row?.since),
    waited,
    onStage: still || !sub ? null : ago(row?.stage_at),
    count: ready ? 'starts when a prep slot frees' : fig?.count ?? dl?.count ?? null, left: fig?.left ?? null,
    pct: fig?.pct ?? dl?.pct ?? null,
  };
}

/** The AI cut step's download of the editor's film for its small preview (R130; tools/autoprep/worker.py
 *  editor_download, the bytes the step has of the film's total): `count` in words ("downloading the editor's film for
 *  its small preview, 1.2 of 3.4 GB") and `pct` its share (0 to 99), or null when the step reports none. */
export function editorDownload(progress) {
  const { bytes, total } = progress || {};
  if (typeof bytes !== 'number' || typeof total !== 'number' || total <= 0 || bytes < 0) return null;
  const got = sizeText(bytes, true), all = sizeText(total);
  const both = got.endsWith(' GB') && all.endsWith(' GB') ? `${got.slice(0, -3)} of ${all}` : `${got} of ${all}`;
  return { count: `downloading the editor's film for its small preview, ${both}`, pct: Math.min(99, (100 * bytes) / total) };
}

/** A training re-prep's step in words, from trainingStep: `head` the step (or the analysis's stage, "Transcribing"),
 *  `rest` the analysis's count, how long and the time left, in a live prep's order ("250 of 1,000 min of audio · 25 min
 *  on this stage · 1 h 10 min on the analysis · about 1 h 15 min left (55 min to 1 h 50 min)"; "4 min so far"; paused,
 *  why; R268: "waited 9 h 53 min for a turn" after the time on the analysis), `text` the two together. A download's own line (the GB, the speed, the time left) is downloadLine's; this is
 *  its fallback. */
export function trainingLine(t) {
  if (!t) return { head: '', rest: null, text: '' };
  if (t.paused) return { head: t.word, rest: t.why, text: t.why ? `${t.word}: ${t.why}` : t.word };
  const head = t.stage || t.word;
  const turn = t.waited && `waited ${t.waited} for a turn`;
  const rest = (t.stage ? [t.count, t.onStage && `${t.onStage} on this stage`, t.onStep && `${t.onStep} on the analysis`, turn, t.left]
    : [t.count, t.onStep && `${t.onStep} so far`, turn, t.left]).filter(Boolean).join(' · ') || null;
  return { head, rest, text: rest ? `${head} · ${rest}` : head };
}

/** The status screen's data with the training re-prep under way in its lane, for the training owner alone: the server
 *  sends `training` only to that account (R123, GET /admin/autoprep-queue), and this adds it only when `owner` is true
 *  too. A downloading re-prep joins Downloading; one being prepared, cut or kept, and a finished one's picture pass or
 *  remake (SIDE_STEPS), join Prepping (`state` the lane's, `training_state` its own). The re-preps kept in the last 3
 *  days (`training_finished`, 2026-10-31) join Ready among the live weddings, newest first. Anyone else's data comes
 *  back as it was given. */
export function withTraining(data, owner) {
  if (!owner || !(data?.training?.length || data?.training_finished?.length)) return data;
  const lanes = (data.training || []).map(t => ({ ...t, kind: 'training', training_state: t.state,
                                                  state: t.state === 'downloading' ? 'downloading' : 'prepping' }));
  const kept = (data.training_finished || []).map(t => ({ ...t, kind: 'training' }));
  const when = r => Date.parse(r.finished_at || '') || 0;
  return { ...data, running: [...(data.running || []), ...lanes],
           finished: [...(data.finished || []), ...kept].sort((a, b) => when(b) - when(a)) };
}

/** The two Macs' names on the status screen. The Mac that runs training only (R218) is Ryan's MacBook Pro, and he
 *  reads the screen from across the room by that name (2026-10-31: "make it say MacBook Pro"). */
export const PREP_MAC = 'Prep Mac';
export const TRAINING_MAC = 'MacBook Pro';

/** Which Mac a row is on, once more than one has checked in: TRAINING_MAC for the Mac that runs training only,
 *  PREP_MAC for the prep Mac. Null with one Mac (nothing to tell apart), or for a row that names no Mac the portal
 *  knows. A Shutter & Sound job (R275) is always SS_WORD: its Mac is Shutter & Sound's, never one of these. */
export function macWord(row, stations) {
  if (row?.kind === 'ss') return SS_WORD;          // (R275) Shutter & Sound's own Mac: always said, it is another company's
  if (!(stations?.length > 1) || !row?.station) return null;
  const st = stations.find(x => x.station === row.station);
  return st ? (st.training_only ? TRAINING_MAC : PREP_MAC) : null;
}

/** The owner's Downloading and Prepping columns split by the Mac that has each wedding (Ryan, 2026-10-31: "top is the
 *  prep mac, bottom is the macbook"): the prep Mac's group, then the training Mac's, each kept even when empty so the
 *  split stays where it was across the room; a wedding no known Mac has comes last, in a group of its own only when
 *  there is one. The column's own order inside each group. Null (no split) unless both kinds of Mac have checked in.
 *  R275: `ss`, the column's Shutter & Sound jobs (ssLanes) while the connection is on (an array, empty or not; null or
 *  left out when off): their own group after the Uncut Gems Macs', kept even when empty as theirs are, so the third
 *  group too stays where it was. With one kind of Mac the Uncut Gems weddings are one group, "Uncut Gems", above it. */
export function byMac(rows, stations, ss = null) {
  const kinds = new Set((stations || []).map(x => !!x.training_only));
  const theirs = Array.isArray(ss) ? [{ key: 'ss', word: SS_WORD, rows: ss }] : [];
  if (kinds.size < 2) return theirs.length ? [{ key: 'ug', word: 'Uncut Gems', rows: [...(rows || [])] }, ...theirs] : null;
  const prep = { key: 'prep', word: PREP_MAC, rows: [] };
  const training = { key: 'training', word: TRAINING_MAC, rows: [] };
  const none = { key: 'none', word: 'No Mac yet', rows: [] };
  for (const r of rows || []) {
    const w = macWord(r, stations);
    (w === PREP_MAC ? prep : w === TRAINING_MAC ? training : none).rows.push(r);
  }
  return [...(none.rows.length ? [prep, training, none] : [prep, training]), ...theirs];
}

// ── Shutter & Sound's training jobs (QUEUE.md R275; Ryan, 2026-11-02: "are we able to port S&S training jobs into the
// live tracker page on the uncut gems portal with a label like we did with 2nd mac so I can see them on the same page?")
// S&S's server reports each job (backend app/ss_jobs.py): an AI cut made on S&S's own Mac when its editor uploads the
// first draft, and its analysis kept here. GET /admin/autoprep-queue sends them as `ss_training` to the training owner
// alone, and only while the S&S connection is on. They take no part in the headline, Up next or Needs you (those are
// the Uncut Gems Macs'): they show in their own group of Downloading and Prepping, and done, in Ready. ──

/** Shutter & Sound's name on the screen: its group, and its jobs' tag ("AI training · Shutter & Sound"). */
export const SS_WORD = 'Shutter & Sound';
/** What each kind of S&S job is, under its title (the card's second line). */
export const SS_JOBS = { ai_cut: 'AI cut at first draft', analysis: 'Its analysis, kept here' };
/** Each stage a running S&S job reports, in full and short (the card's right-hand word). */
export const SS_STAGES = {
  download: { full: 'Downloading the footage', short: 'Downloading' },
  analysis: { full: 'Preparing the analysis', short: 'Analysis' },
  cut: { full: 'Making the AI cut', short: 'AI cut' },
  preview: { full: 'Making the small previews', short: 'Previews' },
  pack: { full: 'Packing the analysis', short: 'Packing' },
  upload: { full: 'Sending it here', short: 'Sending' },
};
const SS_UNIT = { files: ['file', 'files'], clips: ['clip', 'clips'], shots: ['shot', 'shots'], parts: ['part', 'parts'],
                  minutes: ['minute', 'minutes'] };

/** The owner's S&S jobs in the screen's lanes: {downloading, prepping, finished}, each row the server's with `kind`
 *  'ss' (rowKey, kindTag, macWord) and `what` (SS_JOBS). Downloading: running, downloading. Prepping: every other
 *  running job, then the waiting, then the failed (the server keeps a failed one a day). Finished: done, newest first
 *  (`finished_at`), for Ready. Null for anyone else, or while the server sends no `ss_training` (the connection off). */
export function ssLanes(data, owner) {
  if (!owner || !Array.isArray(data?.ss_training)) return null;
  const rows = data.ss_training.map(r => ({ ...r, kind: 'ss', what: SS_JOBS[r.job] || '' }));
  const when = r => Date.parse(r.finished_at || '') || 0;
  return {
    downloading: rows.filter(r => r.state === 'running' && r.stage === 'download'),
    prepping: [...rows.filter(r => r.state === 'running' && r.stage !== 'download'), ...rows.filter(r => r.state === 'waiting'),
               ...rows.filter(r => r.state === 'failed')],
    finished: rows.filter(r => r.state === 'done').sort((a, b) => when(b) - when(a)),
  };
}

/** The status screen's data with the S&S jobs done joining Ready among the rest, newest first (as withTraining does
 *  for the re-preps kept). `lanes` is ssLanes'; null gives the data back as it was. */
export function withSS(data, lanes) {
  if (!lanes?.finished?.length) return data;
  const when = r => Date.parse(r.finished_at || '') || 0;
  return { ...data, finished: [...(data?.finished || []), ...lanes.finished].sort((a, b) => when(b) - when(a)) };
}

/** A working column's count on hover: its Uncut Gems weddings (weddingCount) and its S&S jobs, "2 weddings, 3 orders ·
 *  1 Shutter & Sound"; undefined when the column is empty. */
export function columnTitle(ugRows, ssRows) {
  const parts = [(ugRows || []).length ? weddingCount(ugRows) : null, (ssRows || []).length ? `${ssRows.length} ${SS_WORD}` : null];
  return parts.filter(Boolean).join(' · ') || undefined;
}

/** How many S&S jobs were done in the last 24 hours, for Ready's count (`nowMs` on the portal's clock). */
export function ssReady24(lanes, nowMs = Date.now()) {
  return (lanes?.finished || []).filter(r => nowMs - (Date.parse(r.finished_at || '') || 0) <= 24 * 3600 * 1000).length;
}

/** An S&S job's card, in words: `right` its short step ("AI cut 40%", "Waiting", "Failed"), `line` what is under its
 *  bar (the stage in full, its count, when its count last moved; the waiting words; what went wrong), `pct` the bar's
 *  share or null, `failed`, `quiet` (waiting: a dashed card) and `stale`: "No news from Shutter & Sound for 45 min"
 *  when the server says it has not heard of a waiting or running job for a while (`stale`), else null. */
export function ssStep(row, nowMs = Date.now()) {
  const heard = Date.parse(row?.heard_at || '');
  const stale = row?.stale && Number.isFinite(heard)
    ? `No news from Shutter & Sound for ${durationText(Math.max(0, (nowMs - heard) / 1000))}` : row?.stale ? 'No news from Shutter & Sound' : null;
  if (row?.state === 'failed') {
    return { right: 'Failed', line: row.words || 'It stopped without saying why.', pct: null, failed: true, quiet: false, stale: null };
  }
  if (row?.state === 'waiting') {
    return { right: 'Waiting', line: row.words || "Waiting on Shutter & Sound's Mac", pct: null, failed: false, quiet: true, stale };
  }
  if (row?.state === 'done') {
    return { right: 'Done', line: null, pct: 100, failed: false, quiet: true, stale: null };
  }
  const st = SS_STAGES[row?.stage] || { full: 'Working', short: 'Working' };
  const { done, of, unit } = row || {};
  const counted = typeof done === 'number' && typeof of === 'number' && of > 0 && done >= 0;
  const pct = counted ? Math.min(100, (100 * done) / of) : null;
  let count = null;
  if (counted && unit === 'bytes') {
    const got = sizeText(done, true), all = sizeText(of);
    count = got.endsWith(' GB') && all.endsWith(' GB') ? `${got.slice(0, -3)} of ${all}` : `${got} of ${all}`;
  } else if (counted) {
    const [one, many] = SS_UNIT[unit] || ['', ''];
    count = `${Math.round(done).toLocaleString('en-US')} of ${Math.round(of).toLocaleString('en-US')}${many ? ` ${of === 1 ? one : many}` : ''}`;
  }
  const moved = Date.parse(row?.moved_at || '');
  const movedText = Number.isFinite(moved) ? `moved ${agoShort((nowMs - moved) / 1000)}` : null;
  return { right: pct !== null ? `${st.short} ${percentText(pct)}` : st.short, line: [st.full, count, movedText].filter(Boolean).join(' · '),
           pct, failed: false, quiet: false, stale };
}

/** A training Ready row's "took 3 h 10 min": from its first download to kept (GET /admin/autoprep-queue
 *  `training_finished`: `took_seconds`). Null when that is not known. */
export function tookText(row) {
  const s = row?.took_seconds;
  return typeof s === 'number' && s >= 0 ? `took ${durationText(s)}` : null;
}

/** The board's quiet mark (R209): shown only on an order the server marked `prepped` (it marks one for admin and
 *  editors alone), and only to those two roles. Never words with it. */
export function prepMarkShown(project, role) {
  return project?.prepped === true && (role === 'admin' || role === 'editor');
}

/** The status screen's small tag on a wedding, for the training owner alone: "AI training" on a training re-prep,
 *  "Live job" on everything else (a client's order being prepared now). Null for anyone else: their screen has no tags.
 *  A Shutter & Sound job (R275) is AI training too, its own kind 'ss' (the page adds macWord: "AI training · Shutter &
 *  Sound", as the MacBook's say "AI training · MacBook Pro"). */
export function kindTag(row, owner) {
  if (!owner) return null;
  if (row?.kind === 'ss') return { kind: 'ss', word: 'AI training' };
  return row?.kind === 'training' ? { kind: 'training', word: 'AI training' } : { kind: 'live', word: 'Live job' };
}
